import { Component, OnInit, OnDestroy, AfterViewChecked, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Title, DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import Sortable from 'sortablejs';
import {
  CreatePartnerPageGQL, UpdatePartnerPageGQL, GetSiteGQL, GetPartnerPagesGQL, GetPartnerPageGQL,
  GetPartnerPageVersionsGQL, RevertPartnerPageGQL, PartnerPageVersion, GetSiteNavigationGQL
} from '../../../graphql/graphql';
import { ToastService } from '../../../shared/toast/toast.service';
import { UploadService } from '../../../shared/upload/upload.service';
import slugify from 'slugify';

@Component({
  selector: 'app-page',
  templateUrl: './page.component.html',
  styleUrls: ['./page.component.scss'],
  standalone: false
})
export class PageComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('blocksContainer') blocksContainer?: ElementRef<HTMLElement>;
  private sortableInstance: Sortable | null = null;
  private sortableEl: HTMLElement | null = null;
  pageForm: FormGroup;
  isEdit = false;
  pageId: string | null = null;
  siteId: string = '';
  siteName: string = '';
  siteUrl: string = '';
  existingContent: any = null;
  fullUrlPath: string | null = null;
  isProtectedPage = false;
  private navPages: Map<string, string> = new Map(); // pageId -> slug

  // Version history
  showVersionPanel = false;
  versions: PartnerPageVersion[] = [];
  currentVersionNumber: number | null = null;
  previewVersion: PartnerPageVersion | null = null;
  showRevertModal = false;
  versionToRevert: PartnerPageVersion | null = null;

  blockTypes = [
    { value: 'text', label: 'Text' },
    { value: 'text-image', label: 'Text + Image' },
    { value: 'text-video', label: 'Text + Video' },
    { value: 'team', label: 'Team' },
    { value: 'testimonials', label: 'Testimonials' },
    { value: 'awards', label: 'Awards' }
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private createPartnerPageGQL: CreatePartnerPageGQL,
    private updatePartnerPageGQL: UpdatePartnerPageGQL,
    private getSiteGQL: GetSiteGQL,
    private getPartnerPagesGQL: GetPartnerPagesGQL,
    private getPartnerPageGQL: GetPartnerPageGQL,
    private getPartnerPageVersionsGQL: GetPartnerPageVersionsGQL,
    private revertPartnerPageGQL: RevertPartnerPageGQL,
    private getSiteNavigationGQL: GetSiteNavigationGQL,
    private toast: ToastService,
    private titleService: Title,
    private uploadService: UploadService,
    private sanitizer: DomSanitizer
  ) {
    this.pageForm = this.fb.group({
      name: ['', Validators.required],
      title: ['', Validators.required],
      seoTitle: ['', Validators.required],
      description: ['', Validators.required],
      slug: ['', Validators.required],
      published: [false],
      hidden: [false],
      redirectToChild: [false],
      policy: [false],
      layout: ['default'],
      tagline: [''],
      removeBackground: [false],
      bannerImage: this.fb.group({
        lg: [''],
        md: [''],
        sm: [''],
        alt: ['']
      }),
      blocks: this.fb.array([]),
      sidebarBlocks: this.fb.array([])
    });
  }

  activeTab: 'main' | 'sidebar' = 'main';

  get isHomePage(): boolean {
    const slug = this.pageForm?.get('slug')?.value || '';
    return slug === '/' || slug === 'home';
  }

  get blocks(): FormArray {
    return this.pageForm.get('blocks') as FormArray;
  }

  get sidebarBlocks(): FormArray {
    return this.pageForm.get('sidebarBlocks') as FormArray;
  }

  ngOnInit() {
    this.pageId = this.route.snapshot.params['id'];
    this.isEdit = !!this.pageId;
    this.siteUrl = this.route.parent?.snapshot.params['url'] ||
                   this.route.parent?.parent?.snapshot.params['url'] || '';
    this.loadSiteData();
  }

  loadSiteData() {
    if (this.siteUrl) {
      this.getSiteGQL.watch({ variables: { url: this.siteUrl, categoryId: '' } }).valueChanges.subscribe(result => {
        if (result.data?.site?._id) {
          this.siteId = result.data.site._id;
          this.siteName = result.data.site.client?.name || '';
          this.loadAvailablePages();
          if (this.isEdit) {
            this.loadPageData();
            this.loadVersions();
          } else {
            this.setupSlugGeneration();
          }
        }
      });
    }
  }

  loadPageData() {
    if (this.siteId && this.pageId) {
      this.getPartnerPageGQL.watch({ variables: { id: this.pageId } }).valueChanges.subscribe(result => {
        if (result.data?.partnerPage) {
          const page = result.data.partnerPage;
          this.isProtectedPage = ['404', '/404', 'home', '/'].includes(page.slug || '');
          this.pageForm.patchValue({
            name: page.name,
            title: page.title,
            seoTitle: (page as any).seoTitle || '',
            description: page.description || '',
            slug: page.slug,
            published: page.published,
            hidden: page.hidden || false,
            redirectToChild: (page as any).redirectToChild || false,
            policy: page.policy || false
          });
          if (this.isProtectedPage) {
            this.enforceProtectedValues(page.slug || '');
            this.pageForm.get('published')?.disable();
            this.pageForm.get('hidden')?.disable();
            this.pageForm.get('redirectToChild')?.disable();
          }
          this.updateBannerValidators();
          this.titleService.setTitle(`Pages | ${page.name} | Saltus Partners`);

          if ((page as any).content) {
            const content = (page as any).content;
            this.pageForm.patchValue({
              layout: content.layout || 'default',
              tagline: content.tagline || ''
            });
            if (content.removeBackground) {
              this.pageForm.patchValue({ removeBackground: content.removeBackground });
            }
            if (content.bannerImage) {
              this.pageForm.patchValue({
                bannerImage: {
                  lg: content.bannerImage.lg || '',
                  md: content.bannerImage.md || '',
                  sm: content.bannerImage.sm || '',
                  alt: content.bannerImage.alt || ''
                }
              });
            }
            if (content.main?.blocks) {
              this.blocks.clear();
              content.main.blocks.forEach((block: any) => {
                this.blocks.push(this.createBlockGroup(block));
              });
            }
            if (content.sidebar?.blocks) {
              this.sidebarBlocks.clear();
              content.sidebar.blocks.forEach((block: any) => {
                this.sidebarBlocks.push(this.createBlockGroup(block));
              });
            }
            this.existingContent = content;
          }

          this.setupSlugGeneration();
          this.loadFullUrlPath();
        }
      });
    }
  }

  // Version history
  loadVersions() {
    if (this.pageId) {
      this.getPartnerPageVersionsGQL.watch({ variables: { pageId: this.pageId }, fetchPolicy: 'network-only' }).valueChanges.subscribe(result => {
        if (result.data?.partnerPageVersions) {
          this.versions = result.data.partnerPageVersions as PartnerPageVersion[];
          if (this.versions.length > 0) {
            this.currentVersionNumber = this.versions[0].versionNumber;
          }
        }
      });
    }
  }

  toggleVersionPanel() {
    this.showVersionPanel = !this.showVersionPanel;
    if (this.showVersionPanel && this.versions.length === 0) {
      this.loadVersions();
    }
  }

  previewVersionSnapshot(version: PartnerPageVersion) {
    this.previewVersion = version;
  }

  closePreview() {
    this.previewVersion = null;
  }

  revertToVersion(version: PartnerPageVersion) {
    if (!this.pageId) return;
    this.versionToRevert = version;
    this.showRevertModal = true;
  }

  confirmRevert() {
    const version = this.versionToRevert;
    if (!version || !this.pageId) return;
    this.showRevertModal = false;
    this.versionToRevert = null;

    this.revertPartnerPageGQL.mutate({
      variables: { id: this.pageId, versionNumber: version.versionNumber },
      refetchQueries: [
        { query: this.getPartnerPageGQL.document, variables: { id: this.pageId } },
        { query: this.getPartnerPageVersionsGQL.document, variables: { pageId: this.pageId } }
      ]
    }).subscribe({
      next: () => {
        this.toast.success(`Reverted to version ${version.versionNumber}`);
        this.previewVersion = null;
        this.loadPageData();
        this.loadVersions();
      },
      error: (err) => {
        console.error('Error reverting page:', err);
        this.toast.error('Error reverting page');
      }
    });
  }

  cancelRevert() {
    this.showRevertModal = false;
    this.versionToRevert = null;
  }

  getPreviewBlocks(): any[] {
    return this.previewVersion?.snapshot?.content?.main?.blocks || [];
  }

  createBlockGroup(data?: any): FormGroup {
    const type = data?.type || 'text';
    const isTextImage = type === 'text-image';
    const isTextVideo = type === 'text-video';
    const requireText = isTextImage || isTextVideo || type === 'text';
    const group = this.fb.group({
      type: [type],
      text: [data?.text || '', requireText ? Validators.required : []],
      title: [data?.title || ''],
      videoUrl: [data?.videoUrl || data?.video || '', isTextVideo ? Validators.required : []],
      image: this.fb.group({
        lg: [data?.image?.lg || '', isTextImage ? Validators.required : []],
        md: [data?.image?.md || ''],
        sm: [data?.image?.sm || ''],
        alt: [data?.image?.alt || '', isTextImage ? Validators.required : []]
      }),
      buttons: this.fb.array(
        (data?.buttons || (data?.button?.label ? [data.button] : [])).map((btn: any) => this.createButtonItem(btn))
      ),
      items: this.fb.array(
        (data?.items || []).map((item: any) => this.createTestimonialItem(item))
      ),
      awards: this.fb.array(
        (data?.awards || []).map((award: any) => this.createAwardItem(award))
      )
    });
    return group;
  }

  createTestimonialItem(data?: any): FormGroup {
    return this.fb.group({
      content: [data?.content || ''],
      name: [data?.name || ''],
      location: [data?.location || '']
    });
  }

  createButtonItem(data?: any): FormGroup {
    return this.fb.group({
      label: [data?.label || ''],
      link: [data?.link || '']
    });
  }

  getButtons(blockIndex: number): FormArray {
    return this.blocks.at(blockIndex).get('buttons') as FormArray;
  }

  getButtonsFromBlock(block: any): FormArray {
    return block.get('buttons') as FormArray;
  }

  addButtonToBlock(block: any) {
    (block.get('buttons') as FormArray).push(this.createButtonItem());
  }

  removeButtonFromBlock(block: any, btnIndex: number) {
    (block.get('buttons') as FormArray).removeAt(btnIndex);
  }

  moveButtonInBlock(block: any, from: number, to: number) {
    const arr = block.get('buttons') as FormArray;
    if (to < 0 || to >= arr.length) return;
    const item = arr.at(from);
    arr.removeAt(from);
    arr.insert(to, item);
  }

  addButton(blockIndex: number) {
    this.getButtons(blockIndex).push(this.createButtonItem());
  }

  removeButton(blockIndex: number, btnIndex: number) {
    this.getButtons(blockIndex).removeAt(btnIndex);
  }

  createAwardItem(data?: any): FormGroup {
    return this.fb.group({
      image: this.fb.group({
        lg: [data?.image?.lg || ''],
        md: [data?.image?.md || ''],
        sm: [data?.image?.sm || ''],
        alt: [data?.image?.alt || '']
      })
    });
  }

  getAwards(blockIndex: number): FormArray {
    return this.blocks.at(blockIndex).get('awards') as FormArray;
  }

  getAwardsFromBlock(block: any): FormArray {
    return block.get('awards') as FormArray;
  }

  getSidebarAwards(blockIndex: number): FormArray {
    return this.sidebarBlocks.at(blockIndex).get('awards') as FormArray;
  }

  addAward(blockIndex: number) {
    this.getAwards(blockIndex).push(this.createAwardItem());
  }

  addAwardToBlock(block: any) {
    (block.get('awards') as FormArray).push(this.createAwardItem());
  }

  addSidebarAward(blockIndex: number) {
    this.getSidebarAwards(blockIndex).push(this.createAwardItem());
  }

  removeAward(blockIndex: number, awardIndex: number) {
    this.getAwards(blockIndex).removeAt(awardIndex);
  }

  removeAwardFromBlock(block: any, awardIndex: number) {
    (block.get('awards') as FormArray).removeAt(awardIndex);
  }

  removeSidebarAward(blockIndex: number, awardIndex: number) {
    this.getSidebarAwards(blockIndex).removeAt(awardIndex);
  }

  moveAward(blockIndex: number, from: number, to: number, region: 'main' | 'sidebar' = 'main') {
    const arr = region === 'sidebar' ? this.getSidebarAwards(blockIndex) : this.getAwards(blockIndex);
    if (to < 0 || to >= arr.length) return;
    const fromVal = arr.at(from).value;
    const toVal = arr.at(to).value;
    arr.at(from).patchValue(toVal);
    arr.at(to).patchValue(fromVal);
  }

  moveAwardInBlock(block: any, from: number, to: number) {
    const arr = block.get('awards') as FormArray;
    if (to < 0 || to >= arr.length) return;
    const item = arr.at(from);
    arr.removeAt(from);
    arr.insert(to, item);
  }

  async uploadAwardImage(blockIndex: number, awardIndex: number, region: 'main' | 'sidebar' = 'main') {
    const path = `pages/${this.pageId || 'new'}/awards/`;
    const results = await this.uploadService.pick({ path, imageMax: [1300, 100000] });
    if (results.length) {
      const handle = results[0].url.split('/').pop();
      const base = 'https://cdn.filestackcontent.com';
      const arr = region === 'sidebar' ? this.sidebarBlocks : this.blocks;
      const imageGroup = arr.at(blockIndex).get(['awards', awardIndex, 'image']);
      imageGroup?.patchValue({
        lg: `${base}/resize=width:1300/output=quality:80/${handle}`,
        md: `${base}/resize=width:715/output=quality:80/${handle}`,
        sm: `${base}/resize=width:400/output=quality:80/${handle}`,
      });
    }
  }

  getTestimonialItems(blockIndex: number): FormArray {
    return this.blocks.at(blockIndex).get('items') as FormArray;
  }

  getTestimonialsFromBlock(block: any): FormArray {
    return block.get('items') as FormArray;
  }

  addTestimonialToBlock(block: any) {
    (block.get('items') as FormArray).push(this.createTestimonialItem());
  }

  removeTestimonialFromBlock(block: any, itemIndex: number) {
    (block.get('items') as FormArray).removeAt(itemIndex);
  }

  moveTestimonialInBlock(block: any, from: number, to: number) {
    const arr = block.get('items') as FormArray;
    if (to < 0 || to >= arr.length) return;
    const item = arr.at(from);
    arr.removeAt(from);
    arr.insert(to, item);
  }

  addTestimonialItem(blockIndex: number) {
    this.getTestimonialItems(blockIndex).push(this.createTestimonialItem());
  }

  removeTestimonialItem(blockIndex: number, itemIndex: number) {
    this.getTestimonialItems(blockIndex).removeAt(itemIndex);
  }

  async uploadBannerImage() {
    const path = `pages/${this.pageId || 'new'}/banner/`;
    const results = await this.uploadService.pick({ path, imageMax: [2400, 100000] });
    if (results.length) {
      const handle = results[0].url.split('/').pop();
      const base = 'https://cdn.filestackcontent.com';
      this.pageForm.get('bannerImage')?.patchValue({
        lg: `${base}/resize=width:2400/output=quality:80/${handle}`,
        md: `${base}/resize=width:1200/output=quality:80/${handle}`,
        sm: `${base}/resize=width:600/output=quality:80/${handle}`,
      });
    }
  }

  async uploadBlockImage(blockIndex: number) {
    const path = `pages/${this.pageId || 'new'}/blocks/`;
    const results = await this.uploadService.pick({ path, imageMax: [1302, 100000] });
    if (results.length) {
      const handle = results[0].url.split('/').pop();
      const base = 'https://cdn.filestackcontent.com';
      const imageGroup = this.blocks.at(blockIndex).get('image');
      imageGroup?.patchValue({
        lg: `${base}/resize=width:1302/output=quality:80/${handle}`,
        md: `${base}/resize=width:702/output=quality:80/${handle}`,
        sm: `${base}/resize=width:400/output=quality:80/${handle}`,
      });
    }
  }

  addBlock(type: string = 'text') {
    this.blocks.push(this.createBlockGroup({ type }));
  }

  addSidebarBlock(type: string = 'text') {
    this.sidebarBlocks.push(this.createBlockGroup({ type }));
  }

  removeBlock(index: number) {
    this.blocks.removeAt(index);
  }

  removeSidebarBlock(index: number) {
    this.sidebarBlocks.removeAt(index);
  }

  private buildBlocksPayload(blocks: any[]): any[] {
    return blocks.map((block: any) => {
      const b: any = { type: block.type };
      switch (block.type) {
        case 'text':
          if (block.title) b.title = block.title;
          b.text = block.text;
          break;
        case 'text-image':
          b.title = block.title;
          b.text = block.text;
          if (block.image?.lg || block.image?.md) {
            b.image = block.image;
          }
          b.buttons = (block.buttons || []).filter((btn: any) => btn.label).map((btn: any) => ({
            label: btn.label,
            link: btn.link
          }));
          break;
        case 'text-video':
          b.title = block.title;
          b.text = block.text;
          b.video = (block.videoUrl || '').trim();
          break;
        case 'team':
          break;
        case 'testimonials':
          b.title = block.title;
          b.items = (block.items || []).map((item: any) => ({
            content: item.content,
            name: item.name,
            location: item.location
          }));
          break;
        case 'awards':
          b.title = block.title;
          b.awards = (block.awards || []).map((award: any) => ({
            image: award.image
          }));
          break;
      }
      return b;
    });
  }

  ngAfterViewChecked() {
    const el = this.blocksContainer?.nativeElement;
    if (el && el !== this.sortableEl) {
      this.destroySortable();
      this.sortableEl = el;
      this.sortableInstance = Sortable.create(el, {
        animation: 150,
        handle: '.drag-handle',
        ghostClass: 'sortable-ghost',
        onEnd: (evt) => {
          if (evt.oldIndex !== evt.newIndex) {
            const ctrl = this.blocks.at(evt.oldIndex!);
            this.blocks.removeAt(evt.oldIndex!);
            this.blocks.insert(evt.newIndex!, ctrl);
          }
        }
      });
    }
  }

  ngOnDestroy() {
    this.destroySortable();
  }

  private destroySortable() {
    this.sortableInstance?.destroy();
    this.sortableInstance = null;
    this.sortableEl = null;
  }

  private enforceProtectedValues(slug: string) {
    const is404 = slug === '404' || slug === '/404';
    this.pageForm.patchValue({
      name: is404 ? '404' : 'Home',
      slug: is404 ? '/404' : '/',
      published: true,
      hidden: false,
      redirectToChild: false
    }, { emitEvent: false });
  }

  private updateBannerValidators() {
    const lgControl = this.pageForm.get('bannerImage.lg');
    const altControl = this.pageForm.get('bannerImage.alt');
    if (this.isHomePage) {
      lgControl?.setValidators([Validators.required]);
      altControl?.setValidators([Validators.required]);
    } else {
      lgControl?.clearValidators();
      altControl?.clearValidators();
    }
    lgControl?.updateValueAndValidity();
    altControl?.updateValueAndValidity();
  }

  onSubmit() {
    if (this.pageForm.invalid) {
      this.pageForm.markAllAsTouched();
      return;
    }

    if (this.siteId) {
      if (this.isProtectedPage) {
        this.enforceProtectedValues(this.pageForm.getRawValue().slug);
      }
      const formData = this.pageForm.getRawValue();
      const blocks = this.buildBlocksPayload(formData.blocks);

      const input: any = {
        name: formData.name,
        title: formData.title,
        seoTitle: formData.seoTitle || '',
        description: formData.description || '',
        slug: formData.slug,
        published: formData.published,
        hidden: formData.hidden,
        redirectToChild: formData.redirectToChild,
        policy: formData.policy,
        site: this.siteId,
        content: {
          layout: formData.layout || 'default',
          title: formData.title,
          tagline: formData.tagline || '',
          removeBackground: formData.removeBackground || false,
          bannerImage: formData.bannerImage?.lg ? formData.bannerImage : null,
          main: { blocks },
          sidebar: { blocks: this.buildBlocksPayload(formData.sidebarBlocks) }
        }
      };

      const refetchQueries = [{ query: this.getPartnerPagesGQL.document, variables: { siteId: this.siteId } }];

      if (this.isEdit && this.pageId) {
        this.updatePartnerPageGQL.mutate({ variables: { id: this.pageId, updatePartnerPageInput: input }, refetchQueries }).subscribe({
          next: () => {
            this.toast.success('Page updated');
            this.loadVersions();
          },
          error: (err) => { console.error('Error updating page:', err); this.toast.error('Error updating page'); }
        });
      } else {
        this.createPartnerPageGQL.mutate({ variables: { createPartnerPageInput: input }, refetchQueries }).subscribe({
          next: (result) => {
            this.toast.success('Page created');
            if (!this.isEdit && result.data?.createPartnerPage?._id) {
              this.pageId = result.data.createPartnerPage._id;
              this.isEdit = true;
              this.router.navigate(
                ['sites', this.siteUrl, 'manage', 'pages', 'page', this.pageId],
                { replaceUrl: true }
              );
              this.loadVersions();
            }
          },
          error: (err) => { console.error('Error creating page:', err); this.toast.error('Error creating page'); }
        });
      }
    }
  }

  setupSlugGeneration() {
    this.pageForm.get('name')?.valueChanges.subscribe(name => {
      if (name && !this.isEdit) {
        const baseSlug = slugify(name, { lower: true, strict: true });
        this.pageForm.patchValue({ slug: baseSlug }, { emitEvent: false });
      }
    });
  }

  loadAvailablePages() {
    if (!this.siteId) return;
    this.getPartnerPagesGQL.watch({ variables: { siteId: this.siteId } }).valueChanges.subscribe(result => {
      if (result.data?.partnerPages) {
        this.navPages.clear();
        result.data.partnerPages.forEach(p => this.navPages.set(p._id!, p.slug || ''));
        // Re-resolve URL if we already have nav data
        if (this.isEdit) this.loadFullUrlPath();
      }
    });
  }

  loadFullUrlPath() {
    if (!this.siteUrl || !this.pageId) return;
    this.getSiteNavigationGQL.watch({ variables: { url: this.siteUrl, categoryId: '' } }).valueChanges.subscribe(result => {
      const nav = result.data?.site?.navigation;
      if (!nav?.header) { this.fullUrlPath = null; return; }
      this.fullUrlPath = this.findPageUrl(this.pageId!, nav.header, '') || null;
    });
  }

  private getLeafSlug(slug: string): string {
    const parts = slug.replace(/^\//, '').split('/');
    return parts[parts.length - 1];
  }

  private findPageUrl(pageId: string, items: any[], parentPath: string): string | null {
    for (const item of items) {
      const slug = item.type === 'partnerPage' && item.pageId
        ? this.getLeafSlug(this.navPages.get(item.pageId) || item.url || '')
        : item.url || '';
      const itemPath = parentPath ? parentPath + '/' + slug : '/' + slug;

      if (item.type === 'partnerPage' && item.pageId === pageId) {
        const pageSlug = this.getLeafSlug(this.navPages.get(pageId) || slug);
        return parentPath ? parentPath + '/' + pageSlug : '/' + pageSlug;
      }
      if (item.children?.length) {
        const found = this.findPageUrl(pageId, item.children, itemPath);
        if (found) return found;
      }
    }
    return null;
  }

  getVideoEmbedUrl(url: string): SafeResourceUrl | null {
    if (!url?.trim()) return null;
    try {
      const parsed = new URL(url.trim());
      if (/vimeo\.com/.test(parsed.hostname)) {
        const id = parsed.pathname.match(/(?:video\/)?(\d+)/)?.[1];
        return id ? this.sanitizer.bypassSecurityTrustResourceUrl(`https://player.vimeo.com/video/${id}`) : null;
      }
      if (/(?:youtube\.com|youtu\.be)/.test(parsed.hostname)) {
        let id = parsed.searchParams.get('v');
        if (!id) id = parsed.pathname.match(/^\/(embed|shorts|live|v)\/([\w-]+)/)?.[2] || null;
        if (!id && parsed.hostname === 'youtu.be') id = parsed.pathname.slice(1).split('/')[0];
        return id ? this.sanitizer.bypassSecurityTrustResourceUrl(`https://www.youtube-nocookie.com/embed/${id}`) : null;
      }
    } catch {}
    return null;
  }

  onCancel() {
    this.navigateBack();
  }

  private navigateBack() {
    if (this.isEdit) {
      this.router.navigate(['../../'], { relativeTo: this.route });
    } else {
      this.router.navigate(['../'], { relativeTo: this.route });
    }
  }
}
